import { useState, useEffect, createContext, useContext } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('loading');
  const [superAdminData, setSuperAdminData] = useState(null);
  const [venueAdminData, setVenueAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

// Check user role by looking in Firestore collections
const checkUserRole = async (user) => {
  try {
    console.log('🔍 Checking role for user:', user.uid);
    
    // Check if user is super admin
    const superAdminDoc = await getDoc(doc(db, 'super_admins', user.uid));
    if (superAdminDoc.exists()) {
      setSuperAdminData(superAdminDoc.data());
      setVenueAdminData(null);
      return 'super_admin';
    }

    // Check if user is a venue team member
    const teamMemberQuery = query(
      collection(db, 'venue_team_members'),
      where('memberId', '==', user.uid),
      where('status', '==', 'active')
    );
    const teamMemberSnapshot = await getDocs(teamMemberQuery);
    if (!teamMemberSnapshot.empty) {
      const memberData = teamMemberSnapshot.docs[0].data();
      console.log('👥 Team member found, loading owner admin data for:', memberData.ownerAdminId);

      // Load the owner venue admin's data so the dashboard works transparently
      const ownerQuery = query(
        collection(db, 'venue_admins'),
        where('adminId', '==', memberData.ownerAdminId),
        where('status', '==', 'active')
      );
      const ownerSnapshot = await getDocs(ownerQuery);
      if (!ownerSnapshot.empty) {
        const ownerDoc = ownerSnapshot.docs[0];
        setVenueAdminData({
          id: ownerDoc.id,
          ...ownerDoc.data(),
          // Flag so UI can restrict billing/subscription tabs
          isTeamMember: true,
          teamMemberRole: memberData.role,
          teamMemberName: memberData.name,
        });
        setSuperAdminData(null);
        return 'venue_admin';
      }
    }

    // Check if user is venue admin - need to query by adminId field
    console.log('🔍 Querying venue_admins for adminId:', user.uid);
    const venueAdminsQuery = query(
      collection(db, 'venue_admins'),
      where('adminId', '==', user.uid)
    );
    const venueAdminSnapshot = await getDocs(venueAdminsQuery);
    console.log('📄 Venue admin query result:', venueAdminSnapshot.empty ? 'No docs found' : `Found ${venueAdminSnapshot.docs.length} docs`);
    
    if (!venueAdminSnapshot.empty) {
      const venueAdminDoc = venueAdminSnapshot.docs[0];
      const venueAdminData = {
        id: venueAdminDoc.id,
        ...venueAdminDoc.data()
      };
      
      if (venueAdminData.status === 'active') {
        console.log('✅ Setting venue admin role');
        setVenueAdminData(venueAdminData);
        setSuperAdminData(null);
        return 'venue_admin';
      } else {
        console.log('⏳ Venue admin pending approval:', venueAdminData.status);
        setSuperAdminData(null);
        setVenueAdminData(null);
        return 'venue_admin_pending';
      }
    }

    console.log('❓ No matching admin found, defaulting to mobile_user');
    setSuperAdminData(null);
    setVenueAdminData(null);
    return 'mobile_user';
    
  } catch (error) {
    console.error('❌ Error checking user role:', error);
    return null;
  }
};

  // Log a venue admin session to Firestore
  const logVenueAdminSession = async (user) => {
    try {
      await addDoc(collection(db, 'venue_admin_sessions'), {
        adminId:   user.uid,
        email:     user.email,
        loginAt:   serverTimestamp(),
        userAgent: navigator.userAgent,
        platform:  'portal',
      });
    } catch (e) {
      // Non-critical — don't block login if logging fails
      console.warn('Session log failed:', e.message);
    }
  };

  const refreshUserRole = async () => {
    if (currentUser) {
      const role = await checkUserRole(currentUser);
      setUserRole(role);
    }
  };

  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const role = await checkUserRole(result.user);
      setUserRole(role);
      // Log session for venue admins
      if (role === 'venue_admin') {
        await logVenueAdminSession(result.user);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email, password, userData, skipRoleCheck = false) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Only check role if not explicitly skipped
      if (!skipRoleCheck) {
        const role = await checkUserRole(result.user);
        setUserRole(role);
      }
      
      return result;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
      setUserRole(null);
      setSuperAdminData(null);
      setVenueAdminData(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const role = await checkUserRole(user);
        setUserRole(role);
      } else {
        setUserRole(null);
        setSuperAdminData(null);
        setVenueAdminData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    superAdminData,
    venueAdminData,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}